import React from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  MapPin,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import "./App.css";

export default function App() {
  return (
      <main className="min-h-screen bg-[#070808] text-white overflow-hidden">
        {/* HERO */}
        <section className="relative min-h-screen px-6 py-7 md:px-12 lg:px-20 flex flex-col">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_15%,rgba(255,255,255,0.14),transparent_28%),linear-gradient(115deg,rgba(255,255,255,0.08),transparent_40%)]" />
          <div className="absolute left-[31%] top-0 h-full w-px bg-white/10" />
          <div className="absolute right-[28%] top-0 h-full w-px bg-white/10" />
          <div className="absolute left-[28%] top-24 h-[620px] w-[430px] rotate-[-45deg] bg-gradient-to-b from-white/35 via-white/10 to-transparent opacity-40 blur-[1px]" />

          <nav className="relative z-10 flex items-center justify-between gap-6">
            <a href="#top" className="flex items-center gap-3">
              <img
                  src="/spotwrk-logo.png"
                  alt="Spotwrk logo"
                  className="h-28 md:h-36 w-auto object-contain"
              />
            </a>

            <div className="hidden lg:flex items-center gap-8 text-sm text-white/65">
              <a href="#how" className="hover:text-white transition">How it works</a>
              <a href="#opportunities" className="hover:text-white transition">Opportunities</a>
              <a href="#why" className="hover:text-white transition">Why Spotwrk</a>
            </div>

            <a
                href="#join"
                className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm text-white/85 hover:bg-white hover:text-black transition"
            >
              Join waitlist <ArrowRight size={16} />
            </a>
          </nav>

          <div id="top" className="relative z-10 flex-1 grid lg:grid-cols-[1.03fr_0.97fr] gap-12 items-center pt-14 pb-10">
            <div>
              <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/70 backdrop-blur">
                <Sparkles size={16} /> Built for Detroit creatives first
              </p>

              <h1 className="max-w-5xl text-5xl md:text-7xl lg:text-8xl font-light tracking-[-0.07em] leading-[0.92]">
                Creative opportunities, organized.
              </h1>

              <p className="mt-7 max-w-2xl text-lg md:text-xl text-white/70 leading-relaxed">
                Spotwrk connects creatives with real opportunities — without the chaos of comment sections, scattered Facebook posts, and lost DMs.
              </p>

              <p className="mt-5 text-sm md:text-base text-white/50">
                Built by <span className="text-white/80">Cope Artistry Entertainment</span> in collaboration with <span className="text-white/80">Duhtroit Magazine</span>.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                    href="#join"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 font-semibold text-black hover:bg-white/85 transition"
                >
                  Join early access <ArrowRight size={18} />
                </a>
                <a
                    href="#opportunities"
                    className="inline-flex items-center justify-center rounded-full border border-white/20 px-7 py-4 font-semibold text-white/85 hover:bg-white/10 transition"
                >
                  View sample opportunities
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-10 rounded-full bg-white/10 blur-3xl" />
              <div className="relative rounded-[2rem] border border-white/10 bg-white/[0.04] p-5 md:p-7 backdrop-blur shadow-2xl">
                <div className="rounded-[1.5rem] bg-black/45 border border-white/10 p-5">
                  <div className="flex items-start justify-between border-b border-white/10 pb-4 gap-5">
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em] text-white/40">Featured request</p>
                      <h3 className="mt-2 text-2xl font-semibold">Event Coverage Needed</h3>
                    </div>
                    <span className="rounded-full bg-white text-black px-3 py-1 text-xs font-bold">Open</span>
                  </div>

                  <div className="mt-5 space-y-4 text-sm text-white/75">
                    <InfoRow label="Location" value="Detroit, MI" />
                    <InfoRow label="Budget" value="$400–$600" />
                    <InfoRow label="Date" value="June 28" />
                    <InfoRow label="Looking for" value="Photographer + short-form video" />
                  </div>

                  <p className="mt-5 rounded-2xl bg-white/[0.05] p-4 text-sm leading-relaxed text-white/60">
                    Client is looking for clean candid coverage, detail shots, and a few vertical clips for social media.
                  </p>

                  <button className="mt-6 w-full rounded-full bg-white/10 py-3 font-semibold hover:bg-white hover:text-black transition">
                    I'm interested
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT */}
        <section className="px-6 py-20 md:px-12 lg:px-20 border-t border-white/10 bg-[#0d0e0e]">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.35em] text-white/40">What is Spotwrk?</p>
              <h2 className="mt-4 text-3xl md:text-5xl font-light tracking-[-0.04em]">
                A curated opportunity board for creators.
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed text-white/65">
              Spotwrk helps photographers, videographers, models, stylists, brands, and event organizers connect through structured opportunity requests instead of messy social feeds.
            </p>
          </div>
        </section>

        {/* OPPORTUNITIES */}
        <section id="opportunities" className="px-6 py-20 md:px-12 lg:px-20 bg-black">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <p className="text-sm uppercase tracking-[0.35em] text-white/40">Sample board</p>
                <h2 className="mt-4 text-3xl md:text-5xl font-light tracking-[-0.04em]">
                  Opportunities without the noise.
                </h2>
              </div>
              <p className="max-w-md text-white/55 leading-relaxed">
                These are sample requests for the pilot. The MVP will focus on clear details, moderation, and easy creator interest submissions.
              </p>
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-5">
              <OpportunityCard
                  title="Birthday Dinner Coverage"
                  location="Detroit, MI"
                  budget="$250–$500"
                  detail="Editorial / candid photography"
              />
              <OpportunityCard
                  title="Brand Content Shoot"
                  location="Southfield, MI"
                  budget="$600+"
                  detail="Photographer + reels creator"
              />
              <OpportunityCard
                  title="Fashion Collaboration"
                  location="Royal Oak, MI"
                  budget="TFP / Collab"
                  detail="Models + photographer needed"
              />
            </div>
          </div>
        </section>

        {/* HOW */}
        <section id="how" className="px-6 py-20 md:px-12 lg:px-20 border-t border-white/10 bg-[#0d0e0e]">
          <div className="max-w-6xl mx-auto">
            <p className="text-sm uppercase tracking-[0.35em] text-white/40">How it works</p>
            <h2 className="mt-4 text-3xl md:text-5xl font-light tracking-[-0.04em] max-w-3xl">
              Built small on purpose. Designed to grow with the creative community.
            </h2>

            <div className="mt-12 grid md:grid-cols-3 gap-5">
              <Feature icon={<BriefcaseBusiness />} title="Clients submit requests" text="Requests include budget, date, location, style, and project details." />
              <Feature icon={<Users />} title="Creators discover opportunities" text="Photographers and creatives can find requests that match their style and availability." />
              <Feature icon={<ShieldCheck />} title="Spotwrk keeps it organized" text="Moderated requests and structured workflows help prevent comment-section chaos." />
            </div>
          </div>
        </section>

        {/* WHY */}
        <section id="why" className="px-6 py-20 md:px-12 lg:px-20 bg-black border-t border-white/10">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.35em] text-white/40">Why it exists</p>
              <h2 className="mt-4 text-3xl md:text-5xl font-light tracking-[-0.04em]">
                Creatives deserve better than comment sections.
              </h2>
            </div>
            <div className="space-y-4">
              {[
                "Lost opportunities",
                "Inconsistent lead quality",
                "Spam and low-effort requests",
                "Disorganized posts and inbox chaos",
                "No simple way to track real interest",
              ].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-white/80">
                    <CheckCircle2 className="shrink-0" size={20} /> {item}
                  </div>
              ))}
            </div>
          </div>
        </section>

        {/* JOIN */}
        <section id="join" className="px-6 py-24 md:px-12 lg:px-20 bg-[#0d0e0e] border-t border-white/10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-light tracking-[-0.05em]">Join the early rollout.</h2>
            <p className="mt-5 text-lg text-white/65 leading-relaxed">
              Spotwrk is currently in pilot development. Join the early list to help shape the platform and get notified when the first opportunity board opens.
            </p>

            <form className="mt-9 grid gap-3 text-left">
              <div className="grid md:grid-cols-2 gap-3">
                <input type="text" placeholder="Name" className="rounded-full border border-white/10 bg-black/50 px-6 py-4 outline-none focus:border-white/40" />
                <input type="email" placeholder="Email address" className="rounded-full border border-white/10 bg-black/50 px-6 py-4 outline-none focus:border-white/40" />
              </div>
              <select className="rounded-full border border-white/10 bg-black/50 px-6 py-4 outline-none focus:border-white/40 text-white/70">
                <option>I’m a...</option>
                <option>Photographer</option>
                <option>Videographer</option>
                <option>Model</option>
                <option>Stylist</option>
                <option>Brand / Business</option>
                <option>Other Creative</option>
              </select>
              <button type="button" className="rounded-full bg-white px-7 py-4 font-semibold text-black hover:bg-white/85 transition">
                Join Spotwrk
              </button>
            </form>

            <p className="mt-8 text-xs uppercase tracking-[0.25em] text-white/35">
              Developed by Cope Artistry Entertainment · In collaboration with Duhtroit Magazine
            </p>
          </div>
        </section>
      </main>
  );
}

function InfoRow({ label, value }) {
  return (
      <div className="flex justify-between gap-4">
        <span>{label}</span>
        <strong className="text-white text-right">{value}</strong>
      </div>
  );
}

function OpportunityCard({ title, location, budget, detail }) {
  return (
      <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-6 hover:bg-white/[0.07] transition">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-xl font-semibold leading-tight">{title}</h3>
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/75">Open</span>
        </div>
        <div className="mt-6 space-y-3 text-sm text-white/65">
          <p className="flex items-center gap-2"><MapPin size={16} /> {location}</p>
          <p><span className="text-white/35">Budget:</span> {budget}</p>
          <p><span className="text-white/35">Need:</span> {detail}</p>
        </div>
        <button className="mt-6 w-full rounded-full border border-white/15 py-3 font-semibold hover:bg-white hover:text-black transition">
          Interested
        </button>
      </div>
  );
}

function Feature({ icon, title, text }) {
  return (
      <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-6">
        <div className="mb-5 h-11 w-11 rounded-full bg-white text-black grid place-items-center">
          {React.cloneElement(icon, { size: 21 })}
        </div>
        <h3 className="text-xl font-semibold">{title}</h3>
        <p className="mt-3 text-white/60 leading-relaxed">{text}</p>
      </div>
  );
}
